import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { CDN_ROOT } from '~/core/core.module';
import {
  ActivityFileTypeEnum,
  ActivityTypeEnum,
  ReviewStatInterface,
  SortEnum,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';
import { format, getDate, getMonth } from 'date-fns';
import { map, Subscription } from 'rxjs';

import { BadgeVariantEnum } from '~/component/badge/enums/variant.enum';
import { RatingSizeEnum } from '~/component/vtmn-rating/enums/RatingSize.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { isPlatformBrowser } from '@angular/common';
import { APP_STORAGE } from '~/app.token';
import isBulgaria from '~/core/helpers/country/country.helper';

@Component({
  selector: 'app-attendee-activity-card',
  templateUrl: './activity-card.component.html',
})
export class AttendeeActivityCardComponent implements OnInit {
  @Input() public activity: Activity;
  @Input() public isHp?: boolean = false;

  public organizationName: string;

  public readonly activityTypeEnum = ActivityTypeEnum;
  public readonly badgeVariant = BadgeVariantEnum;
  public readonly informationBadgeVariant = BadgeVariantEnum.GREY_LIGHT;
  public readonly ratingSizeEnum = RatingSizeEnum;
  public reviewsStats: ReviewStatInterface;

  public timeslots: Timeslot[];

  private readonly subscriptions = new Subscription();
  public isMobileDevice: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(CDN_ROOT) private readonly cdnRoot: string,
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly localeService: LocaleService,
    private readonly sportsService: SportsService,
    private readonly activityService: ActivityService,
    private readonly matchMediaService: MatchMediaService,
    private readonly organizationService: OrganizationService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.organizationService
      .checkOrganizationName(this.activity?.organizationIdentifier)
      .then((name) => {
        if (name !== '') {
          this.organizationName = name;
        } else {
          this.organizationService
            .getOrganizationById(this.activity?.organizationIdentifier)
            .subscribe((organization) => {
              const storageKey =
                'organization_' + this.activity?.organizationIdentifier;
              const storageDateKey =
                'organization_' +
                this.activity?.organizationIdentifier +
                '_date';

              this.organizationName = organization.name;
              this.storage?.setItem(storageKey, organization.name);
              this.storage?.setItem(
                storageDateKey,
                JSON.stringify(new Date().getTime())
              );
            });
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      this.activityService
        .getActivityTimeslots(
          this.activity?.sku?.toString(),
          {
            excludeFull: true,
            timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
            sort: {
              order: 'asc' as SortEnum,
              by: 'startDate',
            },
            startDate: new Date().toISOString(),
          },
          1,
          3
        )
        .pipe(map((timeslot) => timeslot.slice(0, 3)))
        .subscribe((timeslots) => {
          this.timeslots = timeslots;
        });
    }
  }

  public get duration(): string {
    return this.activity.formattedDuration(this.localeService.dateLocale);
  }

  public get isBg(): boolean {
    return isBulgaria();
  }

  public get formattedAddress(): string | null {
    const { postalCode, addressLocality } =
      this.activity?.location?.address || {};

    return addressLocality
      ? `${postalCode || ''} ${addressLocality}`.trim()
      : null;
  }

  public get formattedDate(): string {
    const formattedDate: string[] = [];
    let days: number[] = [];
    let previousDate: Date;

    let totalDays = 0;
    this.activity.timeslots?.forEach((timeslot, index) => {
      const date = new Date(timeslot.startDate);
      const day = getDate(date);

      if (previousDate && getMonth(previousDate) !== getMonth(date)) {
        formattedDate.push(this.formatDate(days, previousDate));
        days = [];
      }

      if (!days.includes(day) && totalDays < 3) {
        days.push(day);
        totalDays++;
      }

      if (index + 1 === this.activity.timeslots.length) {
        formattedDate.push(this.formatDate(days, date));
      }

      previousDate = date;
    });

    return formattedDate.join(' ');
  }

  public get ISODuration(): string {
    return this.activity.duration;
  }

  public get link(): string {
    return '';
  }

  private get isHpMobile(): boolean {
    return this.isHp && this.isMobileDevice;
  }

  public get defaultPicture() {
    let defaultImage: string;

    if (this.activity.image?.url?.includes('default.jpg')) {
      let defaultUrl = '-354x236.jpg';
      if (this.isHpMobile) {
        defaultUrl = '-270x180.jpg';
      } else if (!this.isHpMobile) {
        defaultUrl = '-384x256.jpg';
      }
      defaultImage = `${this.cdnRoot}/common/default${defaultUrl}`;
    } else {
      defaultImage = this.activity.image?.url;
    }

    return defaultImage;
  }

  public get picture(): string {
    let activityMediaResolution = ActivityMediaResolutionEnum.LARGE_CARD;

    if (this.isHpMobile) {
      activityMediaResolution = ActivityMediaResolutionEnum.HP_CARD;
    } else if (!this.isHpMobile) {
      activityMediaResolution = ActivityMediaResolutionEnum.CARD;
    }
    return (
      this.activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        activityMediaResolution
      ) || this.defaultPicture
    );
  }

  public get showFullActivityOverlay(): boolean {
    return this.activity.timeslots && this.activity.timeslots.length === 0;
  }

  public get sportName(): string {
    return this.activity.sports
      ?.map((sportId) => this.sportsService.getSport(sportId)?.name)
      .join(', ');
  }

  private formatDate(days: number[], date: Date): string {
    const formattedDays = days.map((day) => {
      if (day < 10) {
        return `0${day}`;
      }

      return day;
    });

    return `${formattedDays.join(', ')} ${format(date, 'MMM', {
      locale: this.localeService.dateLocale,
    })}`;
  }

  isIntersecting(status: boolean) {
    if (status === true) {
      this.activityService
        .getActivityReviewsStats(this.activity.sku, { disableRetry: true })
        .subscribe((reviewsStats) => {
          this.reviewsStats = reviewsStats;
        });
    }
  }
}
